"use client";

import { useEffect, useRef, useState } from "react";
import { PROJECTS_DELIVERED, HAPPY_CUSTOMERS, STATS_UPDATED_AT } from "@/lib/homepageStats";
import StatNumber from "@/components/StatNumber";
import UpdatedAt from "@/components/UpdatedAt";

// A compact, side-column version of StatusSection.tsx's stats block — same real figures
// (from lib/homepageStats, sourced from public/stats.json) and the same count-up-on-scroll
// behavior, but sized to sit next to the "Visit Vilva D2C" button instead of as a full-width
// section of its own.
const MerchantStatsHighlight = () => {
  const [years, setYears] = useState(0);
  const [customers, setCustomers] = useState(0);
  const [projects, setProjects] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      setYears(10);
      setCustomers(HAPPY_CUSTOMERS);
      setProjects(PROJECTS_DELIVERED);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          const duration = 2000;
          const steps = 50;
          const yearsTarget = 10;
          const customersTarget = HAPPY_CUSTOMERS;
          const projectsTarget = PROJECTS_DELIVERED;

          const yearsStep = yearsTarget / steps;
          const customersStep = customersTarget / steps;
          const projectsStep = projectsTarget / steps;
          const intervalTime = duration / steps;

          let currentStep = 0;
          const interval = setInterval(() => {
            if (currentStep >= steps) {
              clearInterval(interval);
              setYears(yearsTarget);
              setCustomers(customersTarget);
              setProjects(projectsTarget);
              return;
            }
            setYears((prev) => Math.min(prev + yearsStep, yearsTarget));
            setCustomers((prev) => Math.min(prev + customersStep, customersTarget));
            setProjects((prev) => Math.min(prev + projectsStep, projectsTarget));
            currentStep++;
          }, intervalTime);
        }
      },
      { threshold: 0 }
    );

    const node = wrapperRef.current;
    if (node) observer.observe(node);

    // Backstop: if the observer never fires, show the real figures rather than zeros.
    const failsafe = window.setTimeout(() => {
      setHasAnimated((already) => {
        if (already) return already;
        setYears(10);
        setCustomers(HAPPY_CUSTOMERS);
        setProjects(PROJECTS_DELIVERED);
        return true;
      });
    }, 4000);

    return () => {
      if (node) observer.unobserve(node);
      window.clearTimeout(failsafe);
    };
  }, [hasAnimated]);

  return (
    <div ref={wrapperRef}>
      <p className="mb-5 text-center font-Schibsted_Grotesk text-[10px] font-bold uppercase tracking-[4px] text-[#FFADA6]">
        Our Track Record
      </p>

      <div className="flex items-start justify-center gap-6 text-center sm:gap-8">
        <div>
          <p className="m-0 font-NeueMontreal text-[28px] font-bold leading-none text-[#7C86FF] sm:text-[32px]">
            {Math.floor(years)}+
          </p>
          <p className="m-0 mt-1.5 font-Schibsted_Grotesk text-[12px] font-medium leading-tight text-white sm:text-[13px]">
            Years in<br />business
          </p>
        </div>

        <div className="mt-1 h-10 w-px shrink-0 bg-white/15" />

        <div>
          <p className="m-0 font-NeueMontreal text-[28px] font-bold leading-none text-[#7C86FF] sm:text-[32px]">
            <StatNumber display={`${Math.floor(customers)}+`} exact={HAPPY_CUSTOMERS} />
          </p>
          <p className="m-0 mt-1.5 font-Schibsted_Grotesk text-[12px] font-medium leading-tight text-white sm:text-[13px]">
            Happy<br />Customers
          </p>
        </div>

        <div className="mt-1 h-10 w-px shrink-0 bg-white/15" />

        <div>
          <p className="m-0 font-NeueMontreal text-[28px] font-bold leading-none text-[#7C86FF] sm:text-[32px]">
            <StatNumber display={`${Math.floor(projects)}+`} exact={PROJECTS_DELIVERED} />
          </p>
          <p className="m-0 mt-1.5 font-Schibsted_Grotesk text-[12px] font-medium leading-tight text-white sm:text-[13px]">
            Projects<br />Delivered
          </p>
        </div>
      </div>

      <UpdatedAt
        iso={STATS_UPDATED_AT}
        className="mt-10 text-center font-Schibsted_Grotesk text-[11px] font-normal text-white/50"
      />
    </div>
  );
};

export default MerchantStatsHighlight;
